import { EmailTemplateDetail } from '@dealstack/frontend/shared/components/EmailTemplates/components/EmailTemplateDetail';
import { EmailTemplateScopeKind } from '@dealstack/frontend/shared/components/EmailTemplates/EmailTemplates.types';
import {
	DEAL_ROUTES,
	DEAL_SETTINGS_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { PageContainer } from '@dealstack/frontend/shared/layout/PageContainer/PageContainer';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav';
import {
	useTranslationAdminDashboard,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEmailTemplate } from '@dealstack/frontend/shared/components/EmailTemplates/hooks/useEmailTemplates';
import { useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

interface Props {
	dealId: string;
}

/**
 * A template opens as its own page, the way a programme template does: the settings sidebar and the
 * "Deal settings" title give way to the editor, and the trail carries the template name. The editor
 * supplies its own header (name, status, reset, save), so the page adds only the breadcrumbs.
 */
export const DealEmailTemplateDetailPage = ({ dealId }: Props) => {
	const navigate = useNavigate();
	const tCommonUi = useTranslationCommonUi();
	const tAdminDashboard = useTranslationAdminDashboard();
	const { templateType = '' } = useParams<{ templateType: string }>();
	const scope = useMemo(() => ({ kind: EmailTemplateScopeKind.DEAL, id: dealId }), [dealId]);

	const settingsUrl = generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.SETTINGS}`, { dealId });
	const listUrl = `${settingsUrl}${DEAL_SETTINGS_ROUTES.EMAIL_TEMPLATES}`;

	const { data: template } = useEmailTemplate({ scope, type: templateType });

	// The template crumb waits for the name: an item with an empty name suppresses the whole trail.
	useBreadcrumbs([
		{
			name: tCommonUi('breadcrumbs.deal_settings'),
			url: settingsUrl,
		},
		{
			name: tAdminDashboard('deal_settings.labels.email_templates', {
				defaultValue: 'Email templates',
			}),
			url: listUrl,
		},
		...(template ? [{ name: template.name, url: '', noLink: true }] : []),
	]);

	return (
		<PageContainer>
			<QuickSwitchBreadcrumbsNav />
			<EmailTemplateDetail
				scope={scope}
				templateType={templateType}
				onBack={() => navigate(listUrl)}
			/>
		</PageContainer>
	);
};
